import { createContext, useContext } from 'react';

export type Lang = 'fr' | 'en';

const fr = {
  'app.name': 'Frogless',
  'app.tagline': 'Le patron vérifié avant la première maille.',
  'app.level': 'niveau 0 · playground',

  'top.examples': 'Exemples',
  'top.new': 'Nouveau patron',
  'top.newHint': 'Coller un patron, ouvrir un fichier texte ou partir d’une trame.',
  'top.examplesPlaceholder': 'Ouvrir un modèle d’exemple…',
  'top.examplesUnavailable': 'Exemples indisponibles',
  'top.lang': 'Langue de l’interface',
  'top.sizes': 'Taille',
  'top.sizesAria': 'Choisir la taille affichée',
  'top.allSizes': 'toutes',
  'top.copyLink': 'Copier le lien',
  'top.download': 'Télécharger le rapport (JSON)',
  'top.downloadShort': 'Rapport JSON',
  'top.clear': 'Effacer',
  'top.clearTitle': 'Vider l’éditeur (Ctrl+Z pour annuler)',

  'engine.stage.downloading-runtime': 'téléchargement du moteur',
  'engine.stage.starting-python': 'démarrage de Python',
  'engine.stage.installing-engine': 'installation de stitchcore',
  'engine.stage.ready': 'Prêt à vérifier',
  'engine.loading': 'Le moteur prépare ses aiguilles…',
  'engine.ready': 'Prêt à vérifier',
  'engine.readyVersion': 'Prêt à vérifier · moteur {version}',
  'engine.compiled': 'Vérifié en {ms} ms',
  'engine.compiling': 'Vérification…',
  'engine.error': 'Erreur du moteur',
  'engine.unavailable': 'Moteur indisponible',
  'engine.mock': 'moteur de démonstration',
  'engine.status': 'État du moteur',

  'banner.mock': 'Moteur de démonstration (mock) : résultats pré-enregistrés pour les exemples, aucun calcul réel.',
  'banner.mockFallback':
    'Moteur de démonstration (mock) : la wheel stitchcore est absente. Construisez-la avec « uv build --package stitchcore » puis relancez pnpm dev.',
  'banner.unavailable': 'Le moteur n’a pas pu démarrer : {message}',
  'banner.useMock': 'Utiliser le moteur de démonstration',
  'banner.compileError': 'Le moteur a échoué sur ce texte : {message}',
  'banner.dismiss': 'Masquer',

  'editor.title': 'Le patron',
  'editor.draft': 'Brouillon gardé sur cet appareil',
  'editor.draftShort': 'Brouillon gardé',
  'editor.draftTitle': 'Le texte est enregistré dans ce navigateur uniquement (localStorage).',
  'editor.draftForget': 'Oublier le brouillon',
  'editor.draftOff': 'Brouillon non gardé',
  'editor.hint': 'Ctrl/⌘ + Entrée : vérifier maintenant',
  'editor.placeholder': 'Collez votre patron ici, ou ouvrez un fichier. Une ligne par rang ; en-tête facultatif «\u00a0# sizes:\u00a0S,\u00a0M,\u00a0L\u00a0».',
  'editor.show': 'Afficher le patron',
  'editor.hide': 'Replier le patron',
  'editor.aria': 'Éditeur du patron',
  'editor.lines': '{n} lignes',

  'doc.untitled': 'Sans titre',
  'doc.round': 'en rond',
  'doc.flat': 'allers-retours',
  'doc.sizes': 'Tailles',
  'doc.gauge': 'Échantillon',
  'doc.noGauge': 'non indiqué',
  'doc.coverage': 'Couverture',
  'doc.coverageDetail': '{n}/{total} rangs compris',
  'doc.patternLang': 'Langue du patron',
  'doc.construction': 'Construction',
  'doc.waiting': 'Le moteur prépare ses aiguilles…',
  'doc.perCm': '{sts} m/cm · {rows} rgs/cm',
  'doc.countTitle': '{label} : {n} (soit {perSize} diagnostics taille par taille)',

  'sev.error': 'Erreur',
  'sev.warning': 'Avertissement',
  'sev.info': 'Info',
  'sevs.error': 'Erreurs',
  'sevs.warning': 'Avertissements',
  'sevs.info': 'Infos',

  'tabs.aria': 'Résultats de la compilation',
  'tabs.rows': 'Rangs',
  'tabs.diagnostics': 'Alertes',
  'tabs.map': 'Carte des mailles',
  'tabs.canonical': 'Texte canonique',

  'rows.line': 'Ligne',
  'rows.label': 'Rang',
  'rows.canonical': 'Forme canonique',
  'rows.showCanonical': 'Forme canonique',
  'rows.final': 'Compte final',
  'rows.empty': 'Pas encore de rang à tricoter à blanc.',
  'rows.notApplicable': 'ne concerne pas cette taille',
  'rows.naTitle': '{size} : ne concerne pas cette taille',
  'rows.resynced': 'reprise sur le compte annoncé',
  'rows.notUnderstood': 'rang partiellement compris',
  'rows.hasError': 'erreur',
  'rows.hasWarning': 'avertissement',
  'rows.caption': 'Mailles en fin de rang, par taille. Cliquez un rang pour aller à la ligne.',
  'rows.cellTitle': '{size} : {available} → {after} m.',
  'rows.legendResynced': 'souligné pointillé : reprise sur le compte annoncé',
  'rows.legendNa': '— : rang d’une autre taille',
  'rows.statusUnverified': 'non vérifié : le compte avant ce rang n’était pas connu',
  'rows.statusIsland': 'lu par îlot, jamais vert tant qu’un compte annoncé ne le confirme pas',
  'rows.statusIslandConfirmed': 'lu par îlot, confirmé par un compte annoncé',
  'rows.statusProposals': 'non lu : des lectures sont proposées dans « Lignes à relire »',
  'rows.statusUnread': 'non lu',
  'rows.statusLegend': 'États de lecture',
  'rows.legendChecked': 'vérifié : exécuté et contrôlé sur un compte connu, erreurs comprises',
  'rows.legendUnverified': 'non vérifié : exécuté, mais le compte d’avant n’était pas connu ; un compte annoncé est pris de confiance',
  'rows.legendIsland': 'lu par îlot : lecture déduite des mots de la ligne, jamais verte tant qu’un compte annoncé ne la confirme pas',
  'rows.legendProposals': 'propositions : rang non lu, mais le moteur propose des lectures (« Lignes à relire »)',
  'rows.legendUnread': 'non lu : rang non exécuté pour cette taille',
  'rows.statusSummary': '{size} : {parts}',
  'rows.statusSummaryTitle': 'États de lecture des rangs pour {size}',
  'rows.checked1': '{n} vérifié',
  'rows.checkedN': '{n} vérifiés',
  'rows.unverified1': '{n} non vérifié',
  'rows.unverifiedN': '{n} non vérifiés',
  'rows.island1': '{n} lu par îlot',
  'rows.islandN': '{n} lus par îlot',
  'rows.proposals1': '{n} avec propositions',
  'rows.proposalsN': '{n} avec propositions',
  'rows.unread1': '{n} non lu',
  'rows.unreadN': '{n} non lus',

  'diag.allSizes': 'toutes tailles',
  'diag.sizedMessage': '{sizes} : {text}',
  'diag.document': 'document',
  'diag.line': 'ligne {n}',
  'diag.goto': 'Aller à la ligne {n}',
  'diag.empty': 'Ça tombe juste pour toutes les tailles.',
  'diag.emptyHint': 'Chaque rang a été tricoté à blanc, maille par maille, taille par taille.',
  'diag.emptyFiltered': 'Rien à signaler pour ce filtre.',
  'diag.filter': 'Filtrer par gravité',
  'diag.sizeFiltered': 'Filtré sur la taille {size}',
  'diag.fixes': 'Pistes de correction',
  'diag.apply': 'Appliquer',
  'diag.applyTitle': 'Remplacer la ligne {n} par la version corrigée',
  'diag.applyDisabled': 'Le moteur ne sait pas exprimer cette correction comme une modification de ligne : à reformuler à la main.',
  'diag.lineChanged': 'La ligne a changé depuis la compilation : correction non appliquée.',
  'diag.applied': 'Correction appliquée à la ligne {n} (Ctrl+Z pour annuler).',
  'diag.expected': 'attendu',
  'diag.actual': 'calculé',
  'diag.cost': 'coût {n}',
  'diag.source': 'Texte source',
  'diag.manual': 'manuelle',

  'map.showing': 'Taille {size}',
  'map.pickSize': 'Première taille affichée : choisissez-en une autre dans la barre du haut.',
  'map.zoomIn': 'Zoom avant',
  'map.zoomOut': 'Zoom arrière',
  'map.reset': 'Ajuster',
  'map.truncated': 'Carte tronquée : seuls les {n} premiers rangs sont dessinés.',
  'map.layers': '{n} rangs dessinés',
  'map.none': 'Pas de carte pour cette taille (aucun rang exécutable).',
  'map.disabled': 'Carte non calculée.',
  'map.legend': 'Légende',
  'map.knit': 'endroit',
  'map.purl': 'envers',
  'map.yo': 'jeté',
  'map.decRight': 'dim. penchée à droite',
  'map.decLeft': 'dim. penchée à gauche',
  'map.decCentre': 'dim. centrée',
  'map.cable': 'croisement de torsade',
  'map.unworked': 'base, non travaillée',
  'map.flagged': 'rang signalé',
  'map.help': 'Molette ou + et − pour zoomer, glisser pour déplacer',
  'map.aria': 'Carte des mailles, taille {size}',

  'canon.kind': 'Type',
  'canon.form': 'Forme canonique (taille 1)',
  'canon.rules': 'Règles appliquées',
  'canon.understood': 'compris',
  'canon.partial': 'partiel',
  'canon.empty': 'Aucun rang à afficher.',
  'canon.caption': 'Retraduction lisible de chaque rang, et règles de langue appliquées (utile pour les patrons en français).',

  'status.privacy': 'Vos patrons restent sur votre machine',
  'status.about': 'à propos',
  'status.schema': 'schéma {v}',
  'status.engine': 'moteur {v}',
  // build
  'status.build': 'version {sha} du {date}',
  'status.buildTitle': 'Le commit construit et le jour de la construction : la version en ligne.',

  'about.title': 'À propos de Frogless',
  'about.intro':
    'Frogless relit votre patron comme une tricoteuse attentive : il tricote chaque rang à blanc, pour chaque taille, et vous montre ce qui ne tombe pas juste avant la première maille.',
  'about.p1title': 'Le calcul décide',
  'about.p1': 'Ce sont des comptes, pas une intelligence artificielle. Même texte, même résultat, à chaque fois.',
  'about.p2title': 'Rien n’est corrigé en douce',
  'about.p2': 'Chaque piste de correction est expliquée et revérifiée. C’est vous qui choisissez de l’appliquer.',
  'about.p3title': 'Votre patron fait foi',
  'about.p3': 'Les comptes que vous annoncez servent de référence : le moteur vérifie que les instructions y mènent.',
  'about.p4title': 'Tout se retrouve',
  'about.p4': 'Chaque alerte pointe une ligne, une taille, la valeur attendue et la valeur calculée.',
  'about.p5title': 'Vos patrons restent chez vous',
  'about.p5':
    'Le moteur tourne dans votre navigateur : pas de compte, pas de serveur, pas de suivi. Un lien de partage transporte le patron après le « # » de l’adresse, jamais envoyé au serveur.',
  'about.close': 'Fermer',

  'toast.linkCopied': 'Lien copié : le patron est dans l’URL, rien n’est envoyé.',
  'toast.linkFailed': 'Copie impossible : le lien est dans la barre d’adresse.',
  'toast.exampleLoaded': 'Exemple « {title} » chargé (Ctrl+Z pour revenir).',
  'toast.cleared': 'Texte effacé (Ctrl+Z pour revenir).',
  'toast.draftForgotten': 'Brouillon local oublié.',
  'toast.restoredFromLink': 'Patron chargé depuis le lien partagé.',
  'toast.noResult': 'Rien à télécharger : aucun résultat de compilation.',
  'toast.exampleFailed': 'Impossible de charger l’exemple.',

  'examples.scenario': 'Scénario',
  'welcome.title': 'Bienvenue à l’atelier',
  'welcome.line1': 'Chaque rang est tricoté à blanc, pour chaque taille, maille par maille.',
  'welcome.line2': 'Rien ne quitte votre navigateur : pas de compte, pas de serveur, pas de suivi.',
  'welcome.line3': 'Rien n’est corrigé sans vous : chaque piste est expliquée, vous choisissez.',
  'welcome.examples': 'Modèles d’exemple',
  'welcome.open': 'Ouvrir « {title} »',
  'examples.expectClean': 'patron propre',
  'examples.expectError1': '1 erreur attendue',
  'examples.expectErrorsN': '{n} erreurs attendues',
  'examples.expectWarning1': '1 avertissement attendu',
  'examples.expectWarningsN': '{n} avertissements attendus',
  'examples.confirmReplace':
    'Remplacer le brouillon ?\n\nLe texte de l’éditeur a été modifié. Il sera remplacé par l’exemple « {title} » (Ctrl+Z pour revenir).',
  'fix.recommended': 'Recommandée',
  'fix.recommendedTitle': 'Proposition la moins coûteuse selon le moteur',
  'fix.others1': '1 autre proposition',
  'fix.othersN': '{n} autres propositions',
  'fix.before': 'avant',
  'fix.after': 'après',
  'fix.change.size': 'taille {size} : {from} → {to}',
  'fix.change.value': '{from} → {to}',
  'fix.change.removed': 'retire « {text} »',
  'fix.change.added': 'ajoute « {text} »',
  'fix.change.replaced': '« {from} » → « {to} »',
  'fix.sourceEdit': 'Modification de la ligne {n}',

  'toast.dismiss': 'Fermer la notification',
  'toast.dismissTitle': 'Fermer (Échap)',

  'trace.title': 'Étapes vérifiées par module',
  'trace.caption': 'Ce patron est vérifié par un module dédié (calendrier de mise en forme, talon…) : une valeur par taille pour chaque étape. Cliquez une étape pour aller à la ligne.',
  'trace.what': 'Élément',
  'trace.step': 'Étape',

  'doc.moduleVerified': 'vérifié par module',
  'doc.moduleDetail': '{n} étapes vérifiées',
  'doc.noneUnderstood': 'aucun rang compris',
  'doc.notVerified': 'non vérifié',
  'doc.notVerifiedTitle': 'Le moteur n’a reconnu aucune instruction : l’absence de diagnostic ne prouve rien.',

  'diag.noneTitle': 'Aucun rang compris',
  'diag.noneBody': 'Le moteur n’a reconnu aucune instruction de rang. Vérifiez la langue (en-tête « # lang: fr ») ou la forme des rangs (« Rang 1 : … »).',
  'diag.noneNote': 'Aucun rang compris : ces diagnostics ne couvrent pas les instructions de rang.',

  'meta.title': 'Frogless — le patron vérifié avant la première maille',
  'meta.description': 'Frogless vérifie votre patron de tricot pendant que vous l’écrivez : chaque rang, chaque taille, maille par maille. Le moteur tourne dans votre navigateur, vos patrons ne le quittent pas.',
  'top.more': 'Plus d’actions',
  'top.print': 'Imprimer le carnet',
  'top.printReader': 'Imprimer ma taille',
  'top.compact': 'Vue compacte',
  'top.compactHint': 'Onglets et tableaux denses, pour les tech editors',
  'theme.label': 'Thème : {mode}',
  'theme.light': 'clair',
  'theme.dark': 'sombre',
  'theme.system': 'système',
  'theme.auto': 'auto',
  'theme.next': 'Cliquer pour passer à « {next} ».',
  'theme.systemHint': 'Suit le réglage clair ou sombre de l’appareil.',
  'theme.menuHint': 'Clair, sombre, puis système',
  'top.mySize': 'Ma taille',
  'top.mySizeHint': 'Lire le patron avec les nombres d’une seule taille',
  'engine.statusLabel': 'État du moteur : {label}',
  'engine.mockShort': 'démo',
  'editor.display': 'Affichage',
  'editor.lineNumbers': 'Numéros de ligne',
  'editor.pdfTitle': 'Collé depuis un PDF ?',
  'editor.pdfBody': 'Plusieurs lignes semblent coupées au milieu d’une phrase ; le moteur lit une instruction par ligne.',
  'editor.pdfJoin': 'Recoller les lignes',
  'editor.pdfDismiss': 'Laisser tel quel',
  'toast.pdfJoined': '{n} coupures recollées (Ctrl+Z pour revenir).',
  'toast.pdfChanged': 'Le texte collé a changé depuis : rien n’a été recollé.',
  'reader.title': 'Ma taille',
  'reader.caption': 'Lecture seule : seuls les nombres de la taille {size} sont gardés. Le moteur vérifie toujours le patron complet.',
  'reader.pick': 'Taille à lire',
  'reader.print': 'Imprimer',
  'reader.back': 'Revenir au patron',
  'reader.notForSize': 'ne concerne pas la taille {size}',
  'reader.fromGroup': 'tiré de « {group} »',
  'reader.oneSize': 'Ce patron n’a qu’une taille : rien à filtrer.',
  'reader.empty': 'Le patron est vide.',
  'reader.aria': 'Patron pour la taille {size}, lecture seule',
  'nb.title': 'Le carnet',
  'nb.nav': 'Sections du carnet',
  'nb.summary': 'Résumé',
  'nb.toFix': 'À corriger',
  'nb.toCheck': 'À vérifier',
  'nb.rows': 'Rangs',
  'nb.map': 'Carte',
  'nb.canonical': 'Texte canonique',
  'nb.rowsTitle': 'Les mailles, rang par rang',
  'nb.stepsTitle': 'Les étapes, taille par taille',
  'nb.mapTitle': 'La carte des mailles',
  'nb.toFixEmpty': 'Rien à corriger.',
  'nb.toCheckEmpty': 'Rien à vérifier.',
  'nb.toCheckShow1': 'Afficher le point à vérifier',
  'nb.toCheckShowN': 'Afficher les {n} points à vérifier',
  'nb.toCheckHide': 'Replier',
  'nb.review': 'À relire',
  'nb.reviewTitle': 'Lignes à relire',

  // « Lignes à relire » (ADR-013, décision 6) : les lignes non lues, triées sans classifieur
  'review.intro':
    'Les lignes que le moteur n’a pas lues, triées sans rien deviner : d’abord celles qu’un compte annoncé plus loin oblige à changer le compte, puis celles qu’il vérifiera, celles qu’un rang plus loin contredit, enfin celles que rien ne vérifie. Une proposition se copie, elle ne s’applique jamais : c’est vous qui décidez.',
  'review.empty': 'Toutes les lignes sont lues.',
  'review.noRows': 'Le moteur n’a trouvé aucun rang dans ce texte.',
  'review.cat.gap': 'change le compte',
  'review.cat.exact': 'compte annoncé ensuite',
  'review.cat.weak': 'un rang plus loin ne tombe pas juste',
  'review.cat.none': 'rien pour la vérifier',
  'review.cat.unknown': 'non lue',
  'review.reason.gapFromTo': 'Un compte annoncé suit : cette ligne doit faire passer de {from} à {to} m.',
  'review.reason.gapBy': 'Un compte annoncé suit : cette ligne doit changer le compte de {gap} m.',
  'review.reason.exactZero': 'Un compte annoncé suit et tombe juste sans elle : d’après lui, cette ligne ne change pas le compte.',
  'review.reason.exact': 'Un compte annoncé suit : il la vérifiera une fois lue, sans dire de combien elle change le compte.',
  'review.reason.weak': 'Aucun compte annoncé ne suit, mais un rang plus loin ne tombe pas juste sur le compte reporté : cette ligne change sans doute le compte.',
  'review.reason.none': 'Rien en aval ne permet de la vérifier : ajoutez un compte annoncé après cette ligne.',
  'review.reason.unknown': 'Le moteur ne dit pas ce qui pourrait vérifier cette ligne.',
  'review.proposals1': 'Proposition',
  'review.proposalsN': '{n} propositions, par rang',
  'review.noProposal': 'Aucune proposition : le moteur n’a trouvé aucune lecture de cette ligne.',
  'review.rank': 'rang {n}',
  'review.reconciles': 'donne le compte annoncé',
  'review.reconcilesNot': 'ne donne pas le compte annoncé',
  'review.oracleSlot': '{slot} : d’après le compte annoncé',
  'review.holeSlot': '{slot} : trou',
  'review.holeTitle': 'Trou : rien dans la ligne ne le dit',
  'review.copy': 'Copier',
  'review.copied': 'Copié',
  'review.copyFailed': 'Copie impossible',
  'review.copyAria': 'Copier la proposition {rank} : {text}',
  'review.copyTitle': 'Copier cette lecture pour la reprendre à la main : rien n’est modifié dans le patron.',
  'review.family.shaping': 'façonnage',
  'review.family.markers': 'façonnage aux marqueurs',
  'review.family.division': 'division du tour',
  'review.family.holders': 'mise en attente',
  'review.family.start': 'montage',
  'review.family.count': 'compte',
  'review.family.length': 'longueur',
  'review.family.reference': 'renvoi à un rang',
  'review.slot.sts': 'nombre de mailles',
  'review.slot.rows': 'nombre de rangs',
  'review.slot.times': 'nombre de fois',
  'review.slot.cm': 'longueur',
  'review.slot.rowref': 'rang renvoyé',
  'review.slot.delta': 'changement de compte',
  'review.slot.yo': 'jetés',
  'review.slot.markers': 'marqueurs',
  'review.slot.shape': 'sens (augmenter ou diminuer)',
  'review.slot.bindOff': 'rabat',
  'review.slot.place': 'emplacement',
  'review.slot.every': 'rythme',
  'review.slot.holder': 'mise en attente',
  'review.slot.until': 'jusqu’à',
  'review.slot.start': 'montage',
  'review.slot.count': 'compte',
  'review.slot.continue': 'continuer',
  'review.slot.repeat': 'répéter',
  'sum.none': 'Le moteur n’a reconnu aucun rang : rien n’est vérifié pour l’instant.',
  'sum.clean': 'Ça tombe juste pour toutes les tailles.',
  'sum.cleanOne': 'Ça tombe juste.',
  'sum.cleanModule': 'Ça tombe juste pour toutes les tailles, étape par étape.',
  'sum.cleanFor': 'Ça tombe juste pour {sizes}.',
  'sum.stated': '{where}{row} annonce {expected} {stitches}, le calcul en donne {actual}.',
  'sum.problem': '{where}{row} demande une correction : {title}.',
  'sum.whereSizes': 'En {sizes}, ',
  'sum.whereAll': 'Dans toutes les tailles, ',
  'sum.line': 'la ligne {n}',
  'sum.lineStart': 'La ligne {n}',
  'sum.more1': 'Et un autre point à corriger.',
  'sum.moreN': 'Et {n} autres points à corriger.',
  'sum.toCheck1': 'Un point reste à vérifier.',
  'sum.toCheckN': '{n} points restent à vérifier.',
  'sum.stitches1': 'maille',
  'sum.stitchesN': 'mailles',
  'sum.sizesAria': 'État par taille',
  'sum.sizeOk': 'juste',
  'sum.sizeErrors1': '1 erreur',
  'sum.sizeErrorsN': '{n} erreurs',
  'sum.sizeUnknown': 'non vérifiée',
  'sum.sizeTitle': 'Taille {size} : {state}',
  'find.document': 'tout le patron',
  'find.calc': 'Le calcul',
  'find.stated': 'annoncé {n}',
  'find.after': 'après',
  'find.afterFix': 'après correction :',
  'find.preview': 'Voir avec la correction',
  'find.proposal': 'Correction proposée',
  'find.others': 'Autres pistes ({n})',
  'find.previewThis': 'Voir',
  'find.previewing': 'En aperçu',
  'find.undo': 'Annuler',
  'find.appliedTitle': 'Corrections appliquées',
  'find.applied': 'Corrigé ligne {n}',
  'find.undone': 'Correction annulée à la ligne {n}.',
  'find.undoFailed': 'La ligne a changé depuis : annulez avec Ctrl+Z dans le patron.',
  'find.dismiss': 'Masquer',
  'find.report': 'Signaler',
  'find.reportFalse': 'Ce n’est pas une erreur',
  'find.reportMisread': 'Ligne mal comprise',
  'find.engineSays': 'Le moteur',
  'find.previewAria': 'Aperçu de la ligne {n} corrigée',
  'find.dependsOn1': 'Dépend de la lecture par îlot de la ligne {lines}.',
  'find.dependsOnN': 'Dépend de la lecture par îlot des lignes {lines}.',
  'find.lowered': 'Descendu en avertissement tant qu’un compte annoncé ne confirme pas cette lecture.',
  'toast.reportCopied': 'Merci ! Signalement copié dans le presse-papiers ; rien n’est envoyé.',
  'toast.reportFailed': 'Copie impossible : le presse-papiers est bloqué.',
  'fix.kind.number': 'Nombre',
  'fix.kind.row_end': 'Fin de rang',
  'fix.kind.stated_count': 'Compte annoncé',
  'fix.kind.deletion': 'Suppression',
  'fix.kind.structure': 'Structure',
  'fix.kind.short_rows': 'Rangs raccourcis',
  'fix.kind.other': 'Autre',
  'report.header': 'Frogless : signalement',
  'report.kind': 'Type',
  'report.code': 'Code',
  'report.line': 'Ligne {n}',
  'report.sizes': 'Tailles',
  'report.message': 'Message',
  'report.engine': 'Moteur',
  'report.note': 'Ce que la ligne veut dire (à compléter) :',
  'report.general': 'Problème général',
  'rows.visualCaption': 'Mailles en fin de rang, taille par taille ; la barre la plus longue vaut {max} m. Cliquez un rang pour le retrouver dans le patron.',
  'rows.stepsCaption': 'Une valeur par taille pour chaque étape vérifiée par le module. Cliquez une étape pour la retrouver dans le patron.',
  'rows.tableView': 'Vue tableau',
  'rows.barTitle': '{size} : {n} m.',
  'rows.castOn': 'Montage',
  'rows.aria': '{label}, ligne {n}',
  'map.size': 'Taille',
  'map.reason.no_rows': 'Pas de rang à dessiner : le patron n’a pas encore de rang que le moteur sait tricoter.',
  'map.reason.module_mode': 'Ce patron décrit des calendriers de façonnage, pas des rangs maille par maille : la carte n’a rien à dessiner.',
  'map.reason.not_understood': 'Les rangs n’ont pas été compris assez finement pour être dessinés maille par maille.',
  'map.reason.too_large': 'La carte serait trop grande pour être dessinée ici.',
  'map.rulerCm': 'cm',
  'map.rulerSts': 'm.',
  'map.rulerRows': 'rgs',
  'map.rulerCmCaption': 'Règle en centimètres d’après l’échantillon ({perCm} m./cm) ; en hauteur, les rangs dessinés.',
  'map.rulerStsCaption': 'Règle en mailles (pas d’échantillon) ; en hauteur, les rangs dessinés.',
  'canon.show': 'Afficher le texte canonique',
  'canon.kind.ops': 'instructions',
  'canon.kind.full': 'rang entier',
  'canon.kind.evendec': 'diminutions réparties',
  'canon.kind.ref': 'renvoi',
  'canon.kind.decuntil': 'diminuer jusqu’à',
  'canon.kind.texture': 'point',
  'canon.kind.unknown': 'inconnu',
  'canon.kind.assert': 'vérification du compte',
  'doc.summaryAria': 'Alertes par gravité',
  'doc.langName.fr': 'en français',
  'doc.langName.en': 'en anglais',
  'doc.coverageMeter': '{pct} des rangs compris',
  'welcome.kicker': 'Frogless, niveau 0',
  'welcome.lede': 'Écrivez ou collez votre patron à gauche. Le carnet, à droite, le relit comme une tricoteuse attentive et vous dit ce qui ne tombe pas juste.',
  'welcome.examplesHint': 'Chaque modèle raconte un cas : une erreur de compte, une répétition qui ne tombe pas juste, un talon, un devant de gilet.',
  'about.report': 'Signaler une ligne mal comprise',
  'about.reportHint': 'Copie un modèle de signalement (avec les versions) dans le presse-papiers ; rien n’est envoyé.',
  'status.report': 'signaler',
  'nb.shape': 'Forme',
  'sum.shape': 'Forme : {text}',
  'nb.shapeTitle': 'La forme de la pièce',
  'tabs.shape': 'Forme',
  'shape.view': 'Vue',
  'shape.loading': 'Préparation du schéma…',
  'shape.view2d': 'Vue 2D',
  'shape.view3d': 'Vue 3D',
  'shape.size': 'Taille',
  'shape.missing': 'Ce moteur ne calcule pas encore la forme de la pièce (schéma {version} ; il faut le schéma 1.2.0).',
  'shape.reason.no_rows': 'Pas de forme à dessiner : le patron n’a pas encore de rang que le moteur sait tricoter.',
  'shape.reason.module_mode': 'Ce patron décrit des calendriers de façonnage : la forme n’est pas encore dessinée pour ce type de pièce.',
  'shape.reason.not_understood': 'Les rangs n’ont pas été compris assez finement pour dessiner la forme.',
  'shape.reason.no_width': 'Les largeurs sont inconnues : le moteur n’a pu compter aucune maille.',
  'shape.unavailableFor': 'Taille {size} : {reason}',
  'shape.kind.hat': 'bonnet',
  'shape.kind.sock': 'chaussette',
  'shape.kind.mitten': 'mitaine',
  'shape.kind.moufle': 'moufle',
  'shape.kind.glove': 'gant',
  'shape.kind.cowl': 'snood',
  'shape.kind.scarf': 'écharpe',
  'shape.kind.shawl': 'châle',
  'shape.kind.sleeve': 'manche',
  'shape.kind.body': 'corps',
  'shape.kind.yoke': 'empiècement',
  'shape.kind.blanket': 'couverture',
  'shape.kind.swatch': 'échantillon',
  'shape.kind.unknown': 'pièce',
  'shape.line.round': '{kind} de {around} cm de tour, {height} cm de haut',
  'shape.line.flat': '{kind} de {width} cm de large, {height} cm de haut',
  'shape.line.flatLong': '{kind} de {width} cm de large, {height} cm de long',
  'shape.line.roundLong': '{kind} de {around} cm de tour, {height} cm de long',
  'shape.line.lengthOpen': '{kind} de {width} cm de large, longueur au choix',
  'shape.line.lengthOpenRound': '{kind} de {around} cm de tour, longueur au choix',
  'shape.line.widthOpen': '{kind} de {height} cm de haut, largeur finale non lue (au moins {width} cm)',
  'shape.line.widthOpenLong': '{kind} de {height} cm de long, largeur finale non lue (au moins {width} cm)',
  'shape.line.bothOpen': '{kind}, largeur finale non lue (au moins {width} cm), longueur au choix',
  'shape.line.yoke': 'Empiècement : encolure {neck} cm, {widest} cm au plus large (corps + manches), corps {body} cm sous les manches',
  'shape.line.yokeNoBody': 'Empiècement : encolure {neck} cm, {widest} cm au plus large (corps + manches)',
  'shape.line.fit': '{kind}, {part} de {around} cm de tour{widest}, {height} cm de haut',
  'shape.line.fitLong': '{kind}, {part} de {around} cm de tour{widest}, {height} cm de long',
  'shape.line.fitOpen': '{kind}, {part} de {around} cm de tour{widest}, longueur au choix',
  'shape.line.fitWidest': ' ({v} cm au plus large)',
  'shape.part.brim': 'bord',
  'shape.part.leg': 'jambe',
  'shape.part.hand': 'main',
  'shape.range': '{min} à {max}',
  'shape.schematicAria': 'Schéma de la pièce, taille {size}',
  'shape.schematicAllAria': 'Schéma de la pièce, toutes les tailles superposées',
  'shape.schematicDesc': '{kind} : {width} au plus large, {height} cm de haut.',
  'shape.schematicDescLong': '{kind} : {width} au plus large, {height} cm de long.',
  'shape.around': '{v} cm de tour',
  'shape.aroundShort': 'tour',
  'shape.flatWidth': '{v} cm à plat',
  'shape.widthShort': 'largeur',
  'shape.cm': '{v} cm',
  'shape.castOn': 'montage',
  'shape.widest': 'au plus large',
  'shape.widestYoke': 'au plus large (corps + manches)',
  'shape.neck': 'encolure',
  'shape.bottom': 'en bas',
  'shape.top': 'en haut',
  'shape.gathered': 'resserré sur {n} m.',
  'shape.gatheredShort': 'resserré',
  'shape.drawnFlat': 'Pièce tricotée en rond, dessinée à plat : la largeur dessinée est la moitié du tour.',
  'shape.gridCaption': 'Quadrillage : {n} cm.',
  'shape.lengthOpenCaption': 'Longueur au choix : le schéma montre les rangs lus.',
  'shape.legend': 'Légende',
  'shape.legendSelected': 'taille {size}',
  'shape.legendUncertain': 'estimé : rang non lu, valeur reprise du rang précédent',
  'shape.table': 'Les mesures, taille par taille',
  'shape.col.size': 'Taille',
  'shape.col.castOn': 'Au montage',
  'shape.col.widest': 'Au plus large',
  'shape.col.height': 'Hauteur',
  'shape.col.length': 'Longueur',
  'shape.col.asYouLike': 'au choix',
  'shape.atLeast': 'au moins {v}',
  'shape.col.rowsFlat': 'Rangs',
  'shape.col.rowsRound': 'Tours',
  'shape.col.unavailable': 'forme non dessinée',
  'shape.gauge': 'Échantillon utilisé : {sts} m. et {rows} rangs pour 10 cm.',
  'shape.noGauge': 'Sans échantillon dans le patron : dimensions estimées avec {sts} m. et {rows} rangs pour 10 cm.',
  'shape.fig.title': 'À l’échelle, sur une silhouette',
  'shape.fig.aria': '{kind} à l’échelle sur une silhouette ({who}, {measure} {v} cm)',
  'shape.fig.ariaNoMeasure': '{kind} à l’échelle sur une silhouette ({who})',
  'shape.fig.who': 'Silhouette',
  'shape.fig.auto': 'auto : {who}',
  'shape.fig.autoWhy': 'auto : {who} ({reason})',
  'shape.fig.reasonPiece': '{kind} de {cm} cm',
  'shape.fig.reasonSize': 'taille {size}',
  'shape.fig.reasonOneSize': 'taille unique',
  'shape.fig.captionAuto': 'Silhouette choisie automatiquement : {who} ({reason}), {measure} {v} cm.',
  'shape.fig.captionAutoNoMeasure': 'Silhouette choisie automatiquement : {who} ({reason}).',
  'shape.fig.adult': 'adulte',
  'shape.fig.child': 'enfant',
  'shape.fig.baby': 'bébé',
  'shape.fig.mine': 'mes mesures',
  'shape.fig.caption': 'Silhouette : {who}, {measure} {v} cm.',
  'shape.fig.captionNoMeasure': 'Silhouette : {who}.',
  'shape.fig.source': 'Mesures standard d’après les tableaux du Craft Yarn Council.',
  'shape.fig.scale': '{n} cm',
  'shape.fig.flatAcross': 'Trop courte pour faire le tour du cou : dessinée à plat devant la silhouette.',
  'shape.fig.flatShawl': 'Dessiné à plat dans le dos, le côté le plus large sur les épaules.',
  'shape.fig.scarfHang': 'Portée sans nœud : {n} cm pendent de chaque côté.',
  'shape.fig.widthOpen': 'Largeur finale non lue : silhouette non dessinée.',
  'shape.fig.lengthOpenDrawn': 'Pièce dessinée à {n} cm, longueur au choix.',
  'shape.fig.lengthOpen': 'Longueur au choix : la pièce est dessinée sur les rangs lus.',
  'shape.fig.yoke': 'Empiècement dessiné de l’encolure aux aisselles, le corps en dessous.',
  'shape.measure.head': 'tour de tête',
  'shape.measure.hand': 'tour de main',
  'shape.measure.foot': 'tour de pied',
  'shape.measure.chest': 'tour de poitrine',
  'shape.measure.arm': 'tour de bras',
  'shape.mine': 'Mon {measure} (cm)',
  'shape.ease': 'Aisance : {v} cm',
  'shape.easeDetail': 'pièce {piece} cm, {measure} {body} cm',
  'shape.easeApprox': 'Aisance : ≈ {v} cm',
  'shape.easeDetailApprox': 'pièce ≈ {piece} cm, {measure} {body} cm',
  'shape.easeIndicative': 'Sans échantillon dans le patron, les mesures et l’aisance ne sont qu’indicatives.',
  'shape.ribNote': 'Bord en côtes : s’étire, mesuré à plat en jersey.',
  'shape.easeSize': 'taille {size}',
  'shape.fit.usual': 'dans l’usage',
  'shape.fit.tight': 'plus serré que d’habitude',
  'shape.fit.loose': 'plus ample que d’habitude',
  'shape.hint.hat': 'Pour un bonnet, 2 à 6 cm d’aisance négative est habituel : il tient sans serrer.',
  'shape.hint.sock': 'Pour une chaussette, environ 10 % d’aisance négative (2 à 3 cm) est habituel.',
  'shape.hint.mitten': 'Pour une mitaine, une moufle ou un gant, 0 à 2 cm d’aisance positive est habituel.',
  'shape.hint.cowl': 'Un snood doit passer la tête : son tour dépasse d’habitude le tour de tête.',
  'shape.hint.body': 'Pour un pull de coupe classique, 5 à 10 cm d’aisance positive à la poitrine.',
  'shape.hint.sleeve': 'Pour une manche, 2 à 5 cm d’aisance positive au bras est habituel.',
  // segments (2D)
  'shape.length': 'longueur',
  'shape.legendRib': 'côtes : s’étirent',
  'shape.legendOpen': 'longueur au choix',
  'shape.dimOpen': '{v} cm, au choix',
  'shape.col.drawnOpen': '{v}, au choix',
  'shape.open.foot': 'Pied dessiné à {n} cm, longueur au choix.',
  'shape.open.leg': 'Jambe dessinée à {n} cm, longueur au choix.',
  'shape.open.body': 'Corps dessiné à {n} cm, longueur au choix.',
  'shape.open.sleeve': 'Manche dessinée à {n} cm, longueur au choix.',
  'shape.open.part': '{part} : {n} cm dessinés, longueur au choix.',
  'shape.fig.yokeSleeves': 'Empiècement dessiné de l’encolure aux aisselles, le corps en dessous, les manches le long des bras.',
  'shape.role.cuff': 'bord',
  'shape.role.brim': 'bord',
  'shape.role.hem': 'bas',
  'shape.role.band': 'bordure',
  'shape.role.leg': 'jambe',
  'shape.role.foot': 'pied',
  'shape.role.body': 'corps',
  'shape.role.hand': 'main',
  'shape.role.sleeve': 'manche',
  'shape.role.neck': 'encolure',
  'shape.role.yoke': 'empiècement',
  'shape.role.heel_flap': 'rabat du talon',
  'shape.role.heel_turn': 'tournant du talon',
  'shape.role.gusset': 'gousset',
  'shape.role.instep': 'dessus du pied',
  'shape.role.toe': 'pointe',
  'shape.role.crown': 'calotte',
  'shape.role.top': 'haut',
  'shape.role.thumb_gusset': 'gousset du pouce',
  'shape.role.thumb': 'pouce',
  'shape.role.finger': 'doigt',
  'shape.role.shoulder': 'épaule',
  'shape.role.panel': 'panneau',
  'shape.role.short_rows': 'rangs raccourcis',
  'shape.role.unknown': 'partie',
  'shape.3d.loading': 'Chargement de l’aperçu 3D…',
  'shape.3d.honest': 'Aperçu géométrique à partir des comptes et de l’échantillon, sans simulation du tissu.',
  'shape.3d.yokeNoSleeves': 'Manches non dessinées : l’empiècement est ramené au tour du corps.',
  'shape.3d.noWebgl': 'Ce navigateur n’affiche pas la 3D (WebGL indisponible) : voici le schéma en 2D.',
  'shape.3d.failed': 'L’aperçu 3D n’a pas pu se charger ({message}) : voici le schéma en 2D.',
  'shape.3d.reset': 'Recentrer',
  'shape.3d.wool': 'Laine',
  'shape.3d.moss': 'mousse',
  'shape.3d.oatmeal': 'avoine',
  'shape.3d.rust': 'rouille',
  'shape.3d.aria': 'Aperçu 3D : {kind}, taille {size}',
  'shape.3d.help': 'Glisser pour tourner, molette pour zoomer',
  // segments (3D)
  'shape.3d.sleeveTwice': 'Le patron décrit une manche : la 3D la pose des deux côtés.',
  'shape.3d.lengthOpenPart': 'Longueur au choix : la 3D la montre à une longueur habituelle.',
  'shape.3d.form': 'La forme dessous est indicative, pas à la mesure.',
  // intake
  'intake.title': 'Votre patron',
  'intake.paste': 'Coller mon patron',
  'intake.pasteHint': 'Depuis un PDF, un courriel ou un document.',
  'intake.file': 'Ouvrir un fichier',
  'intake.fileHint': 'Un fichier .txt ou .md, lu dans votre navigateur : rien n’est envoyé. Texte seulement pour l’instant : pas de PDF, d’image ni de Word.',
  'intake.starter': 'Partir d’une trame',
  'intake.starterHint': 'Un en-tête et trois rangs pour démarrer, à compléter.',
  'intake.private': 'Votre texte reste dans votre navigateur.',
  'intake.fileLoaded': '« {name} » ouvert, lu sur cet appareil (Ctrl+Z pour revenir).',
  'intake.confirmReplace': 'Remplacer le texte ?\n\nLe texte de l’éditeur sera remplacé par le fichier « {name} » (Ctrl+Z pour revenir).',
  'intake.dropHere': 'Déposez le fichier : il est lu ici, rien n’est envoyé.',
  'intake.hint.paste': 'Faites Ctrl+V (ou ⌘V) dans l’éditeur.',
  'intake.hint.pasteTouch': 'Appuyez longuement dans l’éditeur, puis « Coller ».',
  'intake.hint.clipboardEmpty': 'Rien à coller pour l’instant : copiez d’abord le texte de votre patron.',
  'intake.hint.pdf': 'Frogless lit du texte seulement pour l’instant, pas les PDF : ouvrez le PDF, copiez le texte du patron, collez-le ici, puis « Recoller les lignes ».',
  'intake.hint.unsupported': 'Frogless lit du texte seulement pour l’instant : ce fichier n’est pas du texte brut (image, Word, tableur…). Ouvrez-le, copiez son texte et collez-le ici.',
  'intake.hint.tooBig': 'Ce fichier est bien long pour un patron (plus de 1 Mo) : collez seulement le patron.',
  'intake.hint.empty': 'Ce fichier est vide.',
  'intake.hint.readFailed': 'Le fichier n’a pas pu être lu : réessayez, ou copiez-collez son texte.',
  'intake.hint.dismiss': 'Fermer',
  'sum.readOne': 'Le carnet n’a lu qu’un rang sur {total} : les comptes ne sont pas vérifiés.',
  'sum.readFew': 'Le carnet n’a lu que {n} rangs sur {total} : les comptes ne sont pas vérifiés.',
  'sum.readPart': 'Le carnet n’a lu que {n} rangs sur {total} : les comptes ne sont vérifiés qu’en partie.',
  'sum.readSee': 'Voir « {section} ».',
} as const;

export type MessageKey = keyof typeof fr;

const en: Record<MessageKey, string> = {
  'app.name': 'Frogless',
  'app.tagline': 'The pattern, checked before the first stitch.',
  'app.level': 'level 0 · playground',

  'top.examples': 'Examples',
  'top.new': 'New pattern',
  'top.newHint': 'Paste a pattern, open a text file or start from a template.',
  'top.examplesPlaceholder': 'Open an example pattern…',
  'top.examplesUnavailable': 'Examples unavailable',
  'top.lang': 'Interface language',
  'top.sizes': 'Size',
  'top.sizesAria': 'Choose the displayed size',
  'top.allSizes': 'all',
  'top.copyLink': 'Copy link',
  'top.download': 'Download report (JSON)',
  'top.downloadShort': 'JSON report',
  'top.clear': 'Clear',
  'top.clearTitle': 'Empty the editor (Ctrl+Z to undo)',

  'engine.stage.downloading-runtime': 'downloading engine',
  'engine.stage.starting-python': 'starting Python',
  'engine.stage.installing-engine': 'installing stitchcore',
  'engine.stage.ready': 'Ready to check',
  'engine.loading': 'The engine is getting its needles ready…',
  'engine.ready': 'Ready to check',
  'engine.readyVersion': 'Ready to check · engine {version}',
  'engine.compiled': 'Checked in {ms} ms',
  'engine.compiling': 'Checking…',
  'engine.error': 'Engine error',
  'engine.unavailable': 'Engine unavailable',
  'engine.mock': 'demo engine',
  'engine.status': 'Engine status',

  'banner.mock': 'Demo engine (mock): pre-recorded results for the examples, no real computation.',
  'banner.mockFallback':
    'Demo engine (mock): the stitchcore wheel is missing. Build it with “uv build --package stitchcore”, then restart pnpm dev.',
  'banner.unavailable': 'The engine could not start: {message}',
  'banner.useMock': 'Use the demo engine',
  'banner.compileError': 'The engine failed on this text: {message}',
  'banner.dismiss': 'Dismiss',

  'editor.title': 'The pattern',
  'editor.draft': 'Draft kept on this device',
  'editor.draftShort': 'Draft kept',
  'editor.draftTitle': 'The text is saved in this browser only (localStorage).',
  'editor.draftForget': 'Forget draft',
  'editor.draftOff': 'Draft not kept',
  'editor.hint': 'Ctrl/⌘ + Enter: check now',
  'editor.placeholder': 'Paste your pattern here, or open a file. One line per row; optional header “# sizes:\u00a0S,\u00a0M,\u00a0L”.',
  'editor.show': 'Show the pattern',
  'editor.hide': 'Fold the pattern away',
  'editor.aria': 'Pattern editor',
  'editor.lines': '{n} lines',

  'doc.untitled': 'Untitled',
  'doc.round': 'in the round',
  'doc.flat': 'flat',
  'doc.sizes': 'Sizes',
  'doc.gauge': 'Gauge',
  'doc.noGauge': 'not given',
  'doc.coverage': 'Coverage',
  'doc.coverageDetail': '{n}/{total} rows understood',
  'doc.patternLang': 'Pattern language',
  'doc.construction': 'Construction',
  'doc.waiting': 'The engine is getting its needles ready…',
  'doc.perCm': '{sts} sts/cm · {rows} rows/cm',
  'doc.countTitle': '{label}: {n} ({perSize} size-by-size diagnostics)',

  'sev.error': 'Error',
  'sev.warning': 'Warning',
  'sev.info': 'Info',
  'sevs.error': 'Errors',
  'sevs.warning': 'Warnings',
  'sevs.info': 'Infos',

  'tabs.aria': 'Compilation results',
  'tabs.rows': 'Rows',
  'tabs.diagnostics': 'Alerts',
  'tabs.map': 'Stitch map',
  'tabs.canonical': 'Canonical text',

  'rows.line': 'Line',
  'rows.label': 'Row',
  'rows.canonical': 'Canonical form',
  'rows.showCanonical': 'Canonical form',
  'rows.final': 'Final count',
  'rows.empty': 'No row to knit on paper yet.',
  'rows.notApplicable': 'does not apply to this size',
  'rows.naTitle': '{size}: does not apply to this size',
  'rows.resynced': 'resynced on the stated count',
  'rows.notUnderstood': 'row partly understood',
  'rows.hasError': 'error',
  'rows.hasWarning': 'warning',
  'rows.caption': 'Stitches at the end of each row, per size. Click a row to jump to its line.',
  'rows.cellTitle': '{size}: {available} → {after} sts',
  'rows.legendResynced': 'dotted underline: resynced on the stated count',
  'rows.legendNa': '—: row for another size',
  'rows.statusUnverified': 'unverified: the count before this row was not known',
  'rows.statusIsland': 'read by island, never green until a stated count confirms it',
  'rows.statusIslandConfirmed': 'read by island, confirmed by a stated count',
  'rows.statusProposals': 'unread: readings are proposed under “Lines to review”',
  'rows.statusUnread': 'unread',
  'rows.statusLegend': 'Reading states',
  'rows.legendChecked': 'checked: executed and checked against a known count, errors included',
  'rows.legendUnverified': 'unverified: executed, but the count before it was not known; a stated count is taken on trust',
  'rows.legendIsland': 'read by island: a reading inferred from the words of the line, never green until a stated count confirms it',
  'rows.legendProposals': 'proposals: an unread row, but the engine proposes readings (“Lines to review”)',
  'rows.legendUnread': 'unread: not executed for this size',
  'rows.statusSummary': '{size}: {parts}',
  'rows.statusSummaryTitle': 'Reading states of the rows for {size}',
  'rows.checked1': '{n} checked',
  'rows.checkedN': '{n} checked',
  'rows.unverified1': '{n} unverified',
  'rows.unverifiedN': '{n} unverified',
  'rows.island1': '{n} read by island',
  'rows.islandN': '{n} read by island',
  'rows.proposals1': '{n} with proposals',
  'rows.proposalsN': '{n} with proposals',
  'rows.unread1': '{n} unread',
  'rows.unreadN': '{n} unread',

  'diag.allSizes': 'all sizes',
  'diag.sizedMessage': '{sizes}: {text}',
  'diag.document': 'document',
  'diag.line': 'line {n}',
  'diag.goto': 'Go to line {n}',
  'diag.empty': 'It all adds up, in every size.',
  'diag.emptyHint': 'Every row was knitted on paper, stitch by stitch, size by size.',
  'diag.emptyFiltered': 'Nothing to report for this filter.',
  'diag.filter': 'Filter by severity',
  'diag.sizeFiltered': 'Filtered on size {size}',
  'diag.fixes': 'Ways to fix it',
  'diag.apply': 'Apply',
  'diag.applyTitle': 'Replace line {n} with the corrected version',
  'diag.applyDisabled': 'The engine cannot express this fix as a line edit: rewrite it by hand.',
  'diag.lineChanged': 'The line changed since compilation: fix not applied.',
  'diag.applied': 'Fix applied to line {n} (Ctrl+Z to undo).',
  'diag.expected': 'expected',
  'diag.actual': 'computed',
  'diag.cost': 'cost {n}',
  'diag.source': 'Source text',
  'diag.manual': 'manual',

  'map.showing': 'Size {size}',
  'map.pickSize': 'First size shown: pick another one in the top bar.',
  'map.zoomIn': 'Zoom in',
  'map.zoomOut': 'Zoom out',
  'map.reset': 'Fit',
  'map.truncated': 'Map truncated: only the first {n} rows are drawn.',
  'map.layers': '{n} rows drawn',
  'map.none': 'No map for this size (no executable rows).',
  'map.disabled': 'Map not computed.',
  'map.legend': 'Legend',
  'map.knit': 'knit',
  'map.purl': 'purl',
  'map.yo': 'yarn over',
  'map.decRight': 'right-leaning dec.',
  'map.decLeft': 'left-leaning dec.',
  'map.decCentre': 'centred dec.',
  'map.cable': 'cable crossing',
  'map.unworked': 'base, unworked',
  'map.flagged': 'flagged row',
  'map.help': 'Wheel or + and − to zoom, drag to pan',
  'map.aria': 'Stitch map, size {size}',

  'canon.kind': 'Kind',
  'canon.form': 'Canonical form (size 1)',
  'canon.rules': 'Rules applied',
  'canon.understood': 'understood',
  'canon.partial': 'partial',
  'canon.empty': 'No rows to show.',
  'canon.caption': 'Readable back-translation of each row and the language rules applied (useful for French patterns).',

  'status.privacy': 'Your patterns stay on your machine',
  'status.about': 'about',
  'status.schema': 'schema {v}',
  'status.engine': 'engine {v}',
  // build
  'status.build': 'build {sha} of {date}',
  'status.buildTitle': 'The commit built and the day it was built: the version that is live.',

  'about.title': 'About Frogless',
  'about.intro':
    'Frogless reads your pattern like a careful knitter: it knits every row on paper, in every size, and shows you what does not add up before the first stitch.',
  'about.p1title': 'Counting decides',
  'about.p1': 'Stitch counts, not artificial intelligence. Same text, same result, every time.',
  'about.p2title': 'Nothing is fixed behind your back',
  'about.p2': 'Every way to fix it is explained and re-checked. You choose whether to apply it.',
  'about.p3title': 'Your pattern is the reference',
  'about.p3': 'The counts you state are the reference: the engine checks that the instructions lead to them.',
  'about.p4title': 'Everything can be traced',
  'about.p4': 'Every alert points to a line, a size, the expected value and the computed one.',
  'about.p5title': 'Your patterns stay with you',
  'about.p5':
    'The engine runs in your browser: no account, no server, no tracking. A share link carries the pattern after the “#” of the address, which is never sent to the server.',
  'about.close': 'Close',

  'toast.linkCopied': 'Link copied: the pattern is in the URL, nothing is uploaded.',
  'toast.linkFailed': 'Could not copy: the link is in the address bar.',
  'toast.exampleLoaded': 'Example “{title}” loaded (Ctrl+Z to go back).',
  'toast.cleared': 'Text cleared (Ctrl+Z to go back).',
  'toast.draftForgotten': 'Local draft forgotten.',
  'toast.restoredFromLink': 'Pattern loaded from the shared link.',
  'toast.noResult': 'Nothing to download: no compilation result.',
  'toast.exampleFailed': 'Could not load the example.',

  'examples.scenario': 'Scenario',
  'welcome.title': 'Welcome to the workshop',
  'welcome.line1': 'Every row is knitted on paper, in every size, stitch by stitch.',
  'welcome.line2': 'Nothing leaves your browser: no account, no server, no tracking.',
  'welcome.line3': 'Nothing is fixed without you: every option is explained, you choose.',
  'welcome.examples': 'Example patterns',
  'welcome.open': 'Open “{title}”',
  'examples.expectClean': 'clean pattern',
  'examples.expectError1': '1 expected error',
  'examples.expectErrorsN': '{n} expected errors',
  'examples.expectWarning1': '1 expected warning',
  'examples.expectWarningsN': '{n} expected warnings',
  'examples.confirmReplace':
    'Replace the draft?\n\nThe editor text was modified. It will be replaced by the example “{title}” (Ctrl+Z to go back).',
  'fix.recommended': 'Recommended',
  'fix.recommendedTitle': 'Cheapest proposal according to the engine',
  'fix.others1': '1 other proposal',
  'fix.othersN': '{n} other proposals',
  'fix.before': 'before',
  'fix.after': 'after',
  'fix.change.size': 'size {size}: {from} → {to}',
  'fix.change.value': '{from} → {to}',
  'fix.change.removed': 'removes “{text}”',
  'fix.change.added': 'adds “{text}”',
  'fix.change.replaced': '“{from}” → “{to}”',
  'fix.sourceEdit': 'Edit of line {n}',

  'toast.dismiss': 'Dismiss notification',
  'toast.dismissTitle': 'Dismiss (Esc)',

  'trace.title': 'Steps verified by a module',
  'trace.caption': 'This pattern is checked by a dedicated module (shaping schedule, heel…): one value per size for each step. Click a step to jump to its line.',
  'trace.what': 'Part',
  'trace.step': 'Step',

  'doc.moduleVerified': 'verified by module',
  'doc.moduleDetail': '{n} steps verified',
  'doc.noneUnderstood': 'no row understood',
  'doc.notVerified': 'not verified',
  'doc.notVerifiedTitle': 'The engine recognised no instruction: the absence of diagnostics proves nothing.',

  'diag.noneTitle': 'No row understood',
  'diag.noneBody': 'The engine did not recognise any row instruction. Check the language (header “# lang: en”) or the row format (“Row 1: …”).',
  'diag.noneNote': 'No row understood: these diagnostics do not cover the row instructions.',

  'meta.title': 'Frogless — the pattern, checked before the first stitch',
  'meta.description': 'Frogless checks your knitting pattern while you write it: every row, every size, stitch by stitch. The engine runs in your browser; your patterns never leave it.',
  'top.more': 'More actions',
  'top.print': 'Print the notebook',
  'top.printReader': 'Print my size',
  'top.compact': 'Compact view',
  'top.compactHint': 'Tabs and dense tables, for tech editors',
  'theme.label': 'Theme: {mode}',
  'theme.light': 'light',
  'theme.dark': 'dark',
  'theme.system': 'system',
  'theme.auto': 'auto',
  'theme.next': 'Click to switch to “{next}”.',
  'theme.systemHint': 'Follows the device’s light or dark setting.',
  'theme.menuHint': 'Light, dark, then system',
  'top.mySize': 'My size',
  'top.mySizeHint': 'Read the pattern with one size’s numbers only',
  'engine.statusLabel': 'Engine status: {label}',
  'engine.mockShort': 'demo',
  'editor.display': 'Display',
  'editor.lineNumbers': 'Line numbers',
  'editor.pdfTitle': 'Pasted from a PDF?',
  'editor.pdfBody': 'Several lines look cut mid-sentence; the engine reads one instruction per line.',
  'editor.pdfJoin': 'Rejoin the lines',
  'editor.pdfDismiss': 'Leave as is',
  'toast.pdfJoined': '{n} breaks rejoined (Ctrl+Z to go back).',
  'toast.pdfChanged': 'The pasted text has changed since: nothing was rejoined.',
  'reader.title': 'My size',
  'reader.caption': 'Read-only: only the numbers for size {size} are kept. The engine still checks the full pattern.',
  'reader.pick': 'Size to read',
  'reader.print': 'Print',
  'reader.back': 'Back to the pattern',
  'reader.notForSize': 'not for size {size}',
  'reader.fromGroup': 'from “{group}”',
  'reader.oneSize': 'This pattern has a single size: nothing to filter.',
  'reader.empty': 'The pattern is empty.',
  'reader.aria': 'Pattern for size {size}, read-only',
  'nb.title': 'The notebook',
  'nb.nav': 'Notebook sections',
  'nb.summary': 'Summary',
  'nb.toFix': 'To fix',
  'nb.toCheck': 'To check',
  'nb.rows': 'Rows',
  'nb.map': 'Map',
  'nb.canonical': 'Canonical text',
  'nb.rowsTitle': 'Stitches, row by row',
  'nb.stepsTitle': 'The steps, size by size',
  'nb.mapTitle': 'The stitch map',
  'nb.toFixEmpty': 'Nothing to fix.',
  'nb.toCheckEmpty': 'Nothing to check.',
  'nb.toCheckShow1': 'Show the point to check',
  'nb.toCheckShowN': 'Show the {n} points to check',
  'nb.toCheckHide': 'Fold away',
  'nb.review': 'To review',
  'nb.reviewTitle': 'Lines to review',

  // “Lines to review” (ADR-013, decision 6): the unread lines, sorted without a classifier
  'review.intro':
    'The lines the engine did not read, sorted without guessing anything: first those a stated count further on says must change the count, then those it will check, those a later row contradicts, and last those nothing can check. A proposal can be copied, it is never applied: you decide.',
  'review.empty': 'Every line is read.',
  'review.noRows': 'The engine found no row in this text.',
  'review.cat.gap': 'changes the count',
  'review.cat.exact': 'stated count next',
  'review.cat.weak': 'a later row does not work out',
  'review.cat.none': 'nothing can check it',
  'review.cat.unknown': 'not read',
  'review.reason.gapFromTo': 'A stated count follows: this line must take the count from {from} to {to} sts.',
  'review.reason.gapBy': 'A stated count follows: this line must change the count by {gap} sts.',
  'review.reason.exactZero': 'A stated count follows and works out without it: by that count, this line does not change the count.',
  'review.reason.exact': 'A stated count follows: it will check the line once read, without telling by how much it changes the count.',
  'review.reason.weak': 'No stated count follows, but a later row does not work out on the carried count: this line probably changes the count.',
  'review.reason.none': 'Nothing further on can check it: add a stated count after this line.',
  'review.reason.unknown': 'The engine does not say what could check this line.',
  'review.proposals1': 'Proposal',
  'review.proposalsN': '{n} proposals, by rank',
  'review.noProposal': 'No proposal: the engine found no reading of this line.',
  'review.rank': 'rank {n}',
  'review.reconciles': 'gives the stated count',
  'review.reconcilesNot': 'does not give the stated count',
  'review.oracleSlot': '{slot}: from the stated count',
  'review.holeSlot': '{slot}: hole',
  'review.holeTitle': 'Hole: nothing in the line says it',
  'review.copy': 'Copy',
  'review.copied': 'Copied',
  'review.copyFailed': 'Could not copy',
  'review.copyAria': 'Copy proposal {rank}: {text}',
  'review.copyTitle': 'Copy this reading to rework it by hand: nothing is changed in the pattern.',
  'review.family.shaping': 'shaping',
  'review.family.markers': 'shaping at markers',
  'review.family.division': 'division of the round',
  'review.family.holders': 'stitches on hold',
  'review.family.start': 'cast-on',
  'review.family.count': 'count',
  'review.family.length': 'length',
  'review.family.reference': 'row reference',
  'review.slot.sts': 'number of stitches',
  'review.slot.rows': 'number of rows',
  'review.slot.times': 'number of times',
  'review.slot.cm': 'length',
  'review.slot.rowref': 'row referred to',
  'review.slot.delta': 'change of count',
  'review.slot.yo': 'yarn overs',
  'review.slot.markers': 'markers',
  'review.slot.shape': 'direction (increase or decrease)',
  'review.slot.bindOff': 'bind-off',
  'review.slot.place': 'place',
  'review.slot.every': 'frequency',
  'review.slot.holder': 'stitches on hold',
  'review.slot.until': 'until',
  'review.slot.start': 'cast-on',
  'review.slot.count': 'count',
  'review.slot.continue': 'continue',
  'review.slot.repeat': 'repeat',
  'sum.none': 'The engine did not recognise any row: nothing is checked yet.',
  'sum.clean': 'It all adds up, in every size.',
  'sum.cleanOne': 'It all adds up.',
  'sum.cleanModule': 'It all adds up in every size, step by step.',
  'sum.cleanFor': 'It all adds up for {sizes}.',
  'sum.stated': '{where}{row} states {expected} {stitches}; the count gives {actual}.',
  'sum.problem': '{where}{row} needs a fix: {title}.',
  'sum.whereSizes': 'In {sizes}, ',
  'sum.whereAll': 'In every size, ',
  'sum.line': 'line {n}',
  'sum.lineStart': 'Line {n}',
  'sum.more1': 'And one more thing to fix.',
  'sum.moreN': 'And {n} more things to fix.',
  'sum.toCheck1': 'One point is left to check.',
  'sum.toCheckN': '{n} points are left to check.',
  'sum.stitches1': 'stitch',
  'sum.stitchesN': 'stitches',
  'sum.sizesAria': 'Status by size',
  'sum.sizeOk': 'adds up',
  'sum.sizeErrors1': '1 error',
  'sum.sizeErrorsN': '{n} errors',
  'sum.sizeUnknown': 'not checked',
  'sum.sizeTitle': 'Size {size}: {state}',
  'find.document': 'whole pattern',
  'find.calc': 'The count',
  'find.stated': 'stated {n}',
  'find.after': 'after',
  'find.afterFix': 'after the fix:',
  'find.preview': 'Show with the fix',
  'find.proposal': 'Suggested fix',
  'find.others': 'Other options ({n})',
  'find.previewThis': 'Preview',
  'find.previewing': 'Previewing',
  'find.undo': 'Undo',
  'find.appliedTitle': 'Applied fixes',
  'find.applied': 'Fixed on line {n}',
  'find.undone': 'Fix undone on line {n}.',
  'find.undoFailed': 'The line changed since: undo with Ctrl+Z in the pattern.',
  'find.dismiss': 'Hide',
  'find.report': 'Report',
  'find.reportFalse': 'This is not an error',
  'find.reportMisread': 'Line misread',
  'find.engineSays': 'The engine',
  'find.previewAria': 'Preview of line {n} with the fix',
  'find.dependsOn1': 'Depends on the island reading of line {lines}.',
  'find.dependsOnN': 'Depends on the island reading of lines {lines}.',
  'find.lowered': 'Lowered to a warning until a stated count confirms this reading.',
  'toast.reportCopied': 'Thank you! Report copied to the clipboard; nothing is sent.',
  'toast.reportFailed': 'Could not copy: the clipboard is blocked.',
  'fix.kind.number': 'Number',
  'fix.kind.row_end': 'Row end',
  'fix.kind.stated_count': 'Stated count',
  'fix.kind.deletion': 'Deletion',
  'fix.kind.structure': 'Structure',
  'fix.kind.short_rows': 'Short rows',
  'fix.kind.other': 'Other',
  'report.header': 'Frogless report',
  'report.kind': 'Kind',
  'report.code': 'Code',
  'report.line': 'Line {n}',
  'report.sizes': 'Sizes',
  'report.message': 'Message',
  'report.engine': 'Engine',
  'report.note': 'What the line means (please fill in):',
  'report.general': 'General issue',
  'rows.visualCaption': 'Stitches at the end of each row, size by size; the longest bar is {max} sts. Click a row to find it in the pattern.',
  'rows.stepsCaption': 'One value per size for each step the module verified. Click a step to find it in the pattern.',
  'rows.tableView': 'Table view',
  'rows.barTitle': '{size}: {n} sts',
  'rows.castOn': 'Cast on',
  'rows.aria': '{label}, line {n}',
  'map.size': 'Size',
  'map.reason.no_rows': 'Nothing to draw: the pattern has no row the engine can knit yet.',
  'map.reason.module_mode': 'This pattern describes shaping schedules, not rows stitch by stitch: the map has nothing to draw.',
  'map.reason.not_understood': 'The rows were not understood finely enough to be drawn stitch by stitch.',
  'map.reason.too_large': 'The map would be too large to draw here.',
  'map.rulerCm': 'cm',
  'map.rulerSts': 'sts',
  'map.rulerRows': 'rows',
  'map.rulerCmCaption': 'Ruler in centimetres from the gauge ({perCm} sts/cm); up the side, the rows drawn.',
  'map.rulerStsCaption': 'Ruler in stitches (no gauge); up the side, the rows drawn.',
  'canon.show': 'Show the canonical text',
  'canon.kind.ops': 'instructions',
  'canon.kind.full': 'whole row',
  'canon.kind.evendec': 'even decreases',
  'canon.kind.ref': 'reference',
  'canon.kind.decuntil': 'decrease until',
  'canon.kind.texture': 'stitch pattern',
  'canon.kind.unknown': 'unknown',
  'canon.kind.assert': 'count check',
  'doc.summaryAria': 'Alerts by severity',
  'doc.langName.fr': 'in French',
  'doc.langName.en': 'in English',
  'doc.coverageMeter': '{pct} of rows understood',
  'welcome.kicker': 'Frogless, level 0',
  'welcome.lede': 'Write or paste your pattern on the left. The notebook, on the right, reads it like a careful knitter and tells you what does not add up.',
  'welcome.examplesHint': 'Each pattern tells a story: a wrong count, a repeat that does not fit, a heel, a vest front.',
  'about.report': 'Report a misread line',
  'about.reportHint': 'Copies a report template (with the versions) to the clipboard; nothing is sent.',
  'status.report': 'report',

  'nb.shape': 'Shape',
  'sum.shape': 'Shape: {text}',
  'nb.shapeTitle': 'The shape of the piece',
  'tabs.shape': 'Shape',
  'shape.view': 'View',
  'shape.loading': 'Preparing the schematic…',
  'shape.view2d': '2D view',
  'shape.view3d': '3D view',
  'shape.size': 'Size',
  'shape.missing': 'This engine does not compute the shape of the piece yet (schema {version}; schema 1.2.0 is needed).',
  'shape.reason.no_rows': 'No shape to draw: the pattern has no row the engine can knit yet.',
  'shape.reason.module_mode': 'This pattern describes shaping schedules: the shape is not drawn yet for this kind of piece.',
  'shape.reason.not_understood': 'The rows were not understood finely enough to draw the shape.',
  'shape.reason.no_width': 'The widths are unknown: the engine could not count any stitch.',
  'shape.unavailableFor': 'Size {size}: {reason}',
  'shape.kind.hat': 'hat',
  'shape.kind.sock': 'sock',
  'shape.kind.mitten': 'mitten',
  'shape.kind.moufle': 'mitten',
  'shape.kind.glove': 'glove',
  'shape.kind.cowl': 'cowl',
  'shape.kind.scarf': 'scarf',
  'shape.kind.shawl': 'shawl',
  'shape.kind.sleeve': 'sleeve',
  'shape.kind.body': 'body',
  'shape.kind.yoke': 'yoke',
  'shape.kind.blanket': 'blanket',
  'shape.kind.swatch': 'swatch',
  'shape.kind.unknown': 'piece',
  'shape.line.round': '{kind}, {around} cm around, {height} cm high',
  'shape.line.flat': '{kind}, {width} cm wide, {height} cm high',
  'shape.line.flatLong': '{kind}, {width} cm wide, {height} cm long',
  'shape.line.roundLong': '{kind}, {around} cm around, {height} cm long',
  'shape.line.lengthOpen': '{kind}, {width} cm wide, length as you like',
  'shape.line.lengthOpenRound': '{kind}, {around} cm around, length as you like',
  'shape.line.widthOpen': '{kind}, {height} cm high, final width not read (at least {width} cm)',
  'shape.line.widthOpenLong': '{kind}, {height} cm long, final width not read (at least {width} cm)',
  'shape.line.bothOpen': '{kind}, final width not read (at least {width} cm), length as you like',
  'shape.line.yoke': 'Yoke: neck {neck} cm, widest {widest} cm (body + sleeves), body {body} cm below the sleeves',
  'shape.line.yokeNoBody': 'Yoke: neck {neck} cm, widest {widest} cm (body + sleeves)',
  'shape.line.fit': '{kind}, {part} {around} cm around{widest}, {height} cm high',
  'shape.line.fitLong': '{kind}, {part} {around} cm around{widest}, {height} cm long',
  'shape.line.fitOpen': '{kind}, {part} {around} cm around{widest}, length as you like',
  'shape.line.fitWidest': ' ({v} cm at the widest)',
  'shape.part.brim': 'brim',
  'shape.part.leg': 'leg',
  'shape.part.hand': 'hand',
  'shape.range': '{min} to {max}',
  'shape.schematicAria': 'Schematic of the piece, size {size}',
  'shape.schematicAllAria': 'Schematic of the piece, all sizes overlaid',
  'shape.schematicDesc': '{kind}: {width} at the widest, {height} cm high.',
  'shape.schematicDescLong': '{kind}: {width} at the widest, {height} cm long.',
  'shape.around': '{v} cm around',
  'shape.aroundShort': 'around',
  'shape.flatWidth': '{v} cm flat',
  'shape.widthShort': 'width',
  'shape.cm': '{v} cm',
  'shape.castOn': 'cast-on',
  'shape.widest': 'widest',
  'shape.widestYoke': 'widest (body + sleeves)',
  'shape.neck': 'neck',
  'shape.bottom': 'bottom',
  'shape.top': 'top',
  'shape.gathered': 'gathered on {n} sts',
  'shape.gatheredShort': 'gathered',
  'shape.drawnFlat': 'Worked in the round, drawn flat: the drawn width is half the circumference.',
  'shape.gridCaption': 'Grid: {n} cm.',
  'shape.lengthOpenCaption': 'Length as you like: the schematic shows the rows read.',
  'shape.legend': 'Legend',
  'shape.legendSelected': 'size {size}',
  'shape.legendUncertain': 'estimated: row not read, value carried over from the previous row',
  'shape.table': 'Measurements, size by size',
  'shape.col.size': 'Size',
  'shape.col.castOn': 'At cast-on',
  'shape.col.widest': 'At the widest',
  'shape.col.height': 'Height',
  'shape.col.length': 'Length',
  'shape.col.asYouLike': 'as you like',
  'shape.atLeast': 'at least {v}',
  'shape.col.rowsFlat': 'Rows',
  'shape.col.rowsRound': 'Rounds',
  'shape.col.unavailable': 'shape not drawn',
  'shape.gauge': 'Gauge used: {sts} sts and {rows} rows to 10 cm.',
  'shape.noGauge': 'No gauge in the pattern: dimensions estimated with {sts} sts and {rows} rows to 10 cm.',
  'shape.fig.title': 'To scale, on a figure',
  'shape.fig.aria': '{kind} to scale on a figure ({who}, {measure} {v} cm)',
  'shape.fig.ariaNoMeasure': '{kind} to scale on a figure ({who})',
  'shape.fig.who': 'Figure',
  'shape.fig.auto': 'auto: {who}',
  'shape.fig.autoWhy': 'auto: {who} ({reason})',
  'shape.fig.reasonPiece': '{kind}, {cm} cm',
  'shape.fig.reasonSize': 'size {size}',
  'shape.fig.reasonOneSize': 'one size',
  'shape.fig.captionAuto': 'Figure chosen automatically: {who} ({reason}), {measure} {v} cm.',
  'shape.fig.captionAutoNoMeasure': 'Figure chosen automatically: {who} ({reason}).',
  'shape.fig.adult': 'adult',
  'shape.fig.child': 'child',
  'shape.fig.baby': 'baby',
  'shape.fig.mine': 'my measurements',
  'shape.fig.caption': 'Figure: {who}, {measure} {v} cm.',
  'shape.fig.captionNoMeasure': 'Figure: {who}.',
  'shape.fig.source': 'Standard measurements after the Craft Yarn Council charts.',
  'shape.fig.scale': '{n} cm',
  'shape.fig.flatAcross': 'Too short to go round the neck: drawn flat in front of the figure.',
  'shape.fig.flatShawl': 'Drawn flat on the back, its widest edge along the shoulders.',
  'shape.fig.scarfHang': 'Worn without a knot: {n} cm hang down each side.',
  'shape.fig.widthOpen': 'Final width not read: not drawn on the figure.',
  'shape.fig.lengthOpenDrawn': 'Drawn at {n} cm, length as you like.',
  'shape.fig.lengthOpen': 'Length as you like: the piece is drawn over the rows read.',
  'shape.fig.yoke': 'Yoke drawn from the neck to the underarms, the body below.',
  'shape.measure.head': 'head circumference',
  'shape.measure.hand': 'hand circumference',
  'shape.measure.foot': 'foot circumference',
  'shape.measure.chest': 'chest',
  'shape.measure.arm': 'upper arm',
  'shape.mine': 'My {measure} (cm)',
  'shape.ease': 'Ease: {v} cm',
  'shape.easeDetail': 'piece {piece} cm, {measure} {body} cm',
  'shape.easeApprox': 'Ease: ≈ {v} cm',
  'shape.easeDetailApprox': 'piece ≈ {piece} cm, {measure} {body} cm',
  'shape.easeIndicative': 'Without a gauge in the pattern, measurements and ease are only indicative.',
  'shape.ribNote': 'Ribbed edge: stretches; measured flat as stockinette.',
  'shape.easeSize': 'size {size}',
  'shape.fit.usual': 'as usual',
  'shape.fit.tight': 'tighter than usual',
  'shape.fit.loose': 'looser than usual',
  'shape.hint.hat': 'For a hat, 2–6 cm of negative ease is usual: it stays on without squeezing.',
  'shape.hint.sock': 'For a sock, about 10 % negative ease (2 to 3 cm) is usual.',
  'shape.hint.mitten': 'For a mitt, a mitten or a glove, 0 to 2 cm of positive ease is usual.',
  'shape.hint.cowl': 'A cowl has to go over the head: it is usually larger around than the head.',
  'shape.hint.body': 'For a classic-fit sweater, 5 to 10 cm of positive ease at the chest.',
  'shape.hint.sleeve': 'For a sleeve, 2 to 5 cm of positive ease at the upper arm is usual.',
  // segments (2D)
  'shape.length': 'length',
  'shape.legendRib': 'ribbing: stretches',
  'shape.legendOpen': 'length as you like',
  'shape.dimOpen': '{v} cm, as you like',
  'shape.col.drawnOpen': '{v}, as you like',
  'shape.open.foot': 'Foot drawn at {n} cm, length as you like.',
  'shape.open.leg': 'Leg drawn at {n} cm, length as you like.',
  'shape.open.body': 'Body drawn at {n} cm, length as you like.',
  'shape.open.sleeve': 'Sleeve drawn at {n} cm, length as you like.',
  'shape.open.part': '{part}: drawn at {n} cm, length as you like.',
  'shape.fig.yokeSleeves': 'Yoke drawn from the neck to the underarms, the body below, the sleeves down the arms.',
  'shape.role.cuff': 'cuff',
  'shape.role.brim': 'brim',
  'shape.role.hem': 'hem',
  'shape.role.band': 'band',
  'shape.role.leg': 'leg',
  'shape.role.foot': 'foot',
  'shape.role.body': 'body',
  'shape.role.hand': 'hand',
  'shape.role.sleeve': 'sleeve',
  'shape.role.neck': 'neck',
  'shape.role.yoke': 'yoke',
  'shape.role.heel_flap': 'heel flap',
  'shape.role.heel_turn': 'heel turn',
  'shape.role.gusset': 'gusset',
  'shape.role.instep': 'instep',
  'shape.role.toe': 'toe',
  'shape.role.crown': 'crown',
  'shape.role.top': 'top',
  'shape.role.thumb_gusset': 'thumb gusset',
  'shape.role.thumb': 'thumb',
  'shape.role.finger': 'finger',
  'shape.role.shoulder': 'shoulder',
  'shape.role.panel': 'panel',
  'shape.role.short_rows': 'short rows',
  'shape.role.unknown': 'part',
  'shape.3d.loading': 'Loading the 3D preview…',
  'shape.3d.honest': 'Geometric preview from the counts and the gauge, without fabric simulation.',
  'shape.3d.yokeNoSleeves': 'Sleeves not drawn: the yoke is reduced to the body circumference.',
  'shape.3d.noWebgl': 'This browser cannot show 3D (WebGL unavailable): here is the 2D schematic.',
  'shape.3d.failed': 'The 3D preview could not load ({message}): here is the 2D schematic.',
  'shape.3d.reset': 'Reset view',
  'shape.3d.wool': 'Yarn',
  'shape.3d.moss': 'moss',
  'shape.3d.oatmeal': 'oatmeal',
  'shape.3d.rust': 'rust',
  'shape.3d.aria': '3D preview: {kind}, size {size}',
  'shape.3d.help': 'Drag to turn, wheel to zoom',
  // segments (3D)
  'shape.3d.sleeveTwice': 'The pattern gives one sleeve: the 3D sets it on both sides.',
  'shape.3d.lengthOpenPart': 'Length as you like: the 3D shows it at a usual length.',
  'shape.3d.form': 'The form underneath is generic, not to measure.',
  // intake
  'intake.title': 'Your pattern',
  'intake.paste': 'Paste my pattern',
  'intake.pasteHint': 'From a PDF, an email or a document.',
  'intake.file': 'Open a file',
  'intake.fileHint': 'A .txt or .md file, read in your browser: nothing is sent. Text only for now: no PDF, image or Word file.',
  'intake.starter': 'Start from a blank',
  'intake.starterHint': 'A header and three rows to get going, ready to fill in.',
  'intake.private': 'Your text stays in your browser.',
  'intake.fileLoaded': '“{name}” opened, read on this device (Ctrl+Z to go back).',
  'intake.confirmReplace': 'Replace the text?\n\nThe editor’s text will be replaced by the file “{name}” (Ctrl+Z to go back).',
  'intake.dropHere': 'Drop the file: it is read here, nothing is sent.',
  'intake.hint.paste': 'Press Ctrl+V (or ⌘V) in the editor.',
  'intake.hint.pasteTouch': 'Press and hold in the editor, then tap Paste.',
  'intake.hint.clipboardEmpty': 'Nothing to paste yet: first copy the text of your pattern.',
  'intake.hint.pdf': 'Frogless reads text only for now, not PDFs: open the PDF, copy the pattern’s text, paste it here, then “Rejoin the lines”.',
  'intake.hint.unsupported': 'Frogless reads text only for now: this file is not plain text (image, Word, spreadsheet…). Open it, copy its text and paste it here.',
  'intake.hint.tooBig': 'This file is very long for a pattern (over 1 MB): paste just the pattern.',
  'intake.hint.empty': 'This file is empty.',
  'intake.hint.readFailed': 'The file could not be read: try again, or copy and paste its text.',
  'intake.hint.dismiss': 'Close',
  'sum.readOne': 'The notebook read only one row out of {total}: the counts are not checked.',
  'sum.readFew': 'The notebook read only {n} rows out of {total}: the counts are not checked.',
  'sum.readPart': 'The notebook read only {n} rows out of {total}: the counts are only partly checked.',
  'sum.readSee': 'See “{section}”.',
};

const DICTS: Record<Lang, Record<MessageKey, string>> = { fr, en };

export type Vars = Record<string, string | number>;
export type Translate = (key: MessageKey, vars?: Vars) => string;

export function translate(lang: Lang, key: MessageKey, vars?: Vars): string {
  const template = DICTS[lang][key] ?? fr[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

export function formatNumber(lang: Lang, n: number, maxFractionDigits = 1): string {
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-GB', { maximumFractionDigits: maxFractionDigits }).format(n);
}

/** 42 → « 42 % » (fr, narrow no-break space) or "42%" (en). */
export function formatPercent(lang: Lang, percent: number): string {
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-GB', { style: 'percent', maximumFractionDigits: 0 }).format(percent / 100);
}

/** ["S", "M", "L"] → « S, M et L » / "S, M and L". */
export function formatList(lang: Lang, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(lang === 'fr' ? 'fr' : 'en-GB', { type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/** « a, b et c » / "a, b and c" as pieces: each item by its index, the words between them as text (to render items as elements). */
export function listParts(lang: Lang, count: number): Array<string | { item: number }> {
  // Private-use marks around each index: no list formatter rewrites them.
  const marks = Array.from({ length: count }, (_, i) => `${i}`);
  return formatList(lang, marks)
    .split(/(\d+)/)
    .map((piece, i) => (i % 2 === 1 ? { item: Number(piece) } : piece))
    .filter((piece) => piece !== '');
}

/**
 * A template split around its placeholders, so a component can render some values as elements
 * (e.g. a row label quoted from the pattern) instead of plain text.
 */
export type TemplatePart = string | { name: string };

export function templateParts(lang: Lang, key: MessageKey): TemplatePart[] {
  const template = DICTS[lang][key] ?? fr[key] ?? key;
  const parts: TemplatePart[] = [];
  let at = 0;
  for (const m of template.matchAll(/\{(\w+)\}/g)) {
    if (m.index > at) parts.push(template.slice(at, m.index));
    parts.push({ name: m[1] });
    at = m.index + m[0].length;
  }
  if (at < template.length) parts.push(template.slice(at));
  return parts;
}

/**
 * French by default. `?lang=en|fr` in the address wins (and is remembered), then the toggle's
 * remembered choice.
 */
export function initialLang(stored: string | null, search = ''): Lang {
  const fromUrl = new URLSearchParams(search).get('lang');
  if (fromUrl === 'en' || fromUrl === 'fr') return fromUrl;
  return stored === 'en' ? 'en' : 'fr';
}

export interface I18n {
  lang: Lang;
  t: Translate;
}

export const I18nContext = createContext<I18n>({ lang: 'fr', t: (key, vars) => translate('fr', key, vars) });

export function useI18n(): I18n {
  return useContext(I18nContext);
}
